import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, MapPin, User, Mail, Phone, MessageSquare, Shield, Award, Sparkles, ChevronRight } from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../admin/lib/supabaseClient';
import type { HireCar } from '../admin/types';
import toast from 'react-hot-toast';

interface BookingFormData {
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  event_date: string;
  pickup_time: string;
  pickup_address: string;
  destination_address: string;
  hire_car_id: string;
  notes: string;
}

export default function MatricDance() {
  const [fleet, setFleet] = useState<HireCar[]>([]);
  const [loadingFleet, setLoadingFleet] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCarId, setSelectedCarId] = useState<string>('');

  const [formData, setFormData] = useState<BookingFormData>({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    event_date: '',
    pickup_time: '',
    pickup_address: '',
    destination_address: '',
    hire_car_id: '',
    notes: ''
  });

  useEffect(() => {
    loadHireFleet();
  }, []);

  const loadHireFleet = async () => {
    setLoadingFleet(true);
    try {
      if (!isSupabaseConfigured) {
        // Fallback demo fleet
        const demoFleet: HireCar[] = [
          {
            id: 'demo-1',
            make: 'Mini',
            model: 'Cooper S Classic',
            year: 1974,
            color: 'British Racing Green',
            description: 'Stunning fully restored green Mini with a white roof, vintage chrome accents, and a pristine beige leather interior. Perfect for classic retro photos.',
            images: [],
            status: 'available',
            created_at: new Date().toISOString()
          },
          {
            id: 'demo-2',
            make: 'Mini',
            model: 'Mk III Classic',
            year: 1969,
            color: 'Tartan Red',
            description: 'Aggressive and beautiful red classic Mini. Features spot lamps, black leather seats, and retro sport stripes. Sure to turn heads at any entrance.',
            images: [],
            status: 'available',
            created_at: new Date().toISOString()
          }
        ];
        setFleet(demoFleet);
        return;
      }

      const { data, error } = await supabase
        .from('hire_cars')
        .select('*')
        .eq('status', 'available')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setFleet(data || []);
    } catch (err) {
      console.error('Error fetching fleet:', err);
      toast.error('Failed to load available Mini fleet');
    } finally {
      setLoadingFleet(false);
    }
  };

  const handleSelectCar = (carId: string) => {
    setSelectedCarId(carId);
    setFormData(prev => ({ ...prev, hire_car_id: carId }));
    // Smooth scroll to form
    document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.hire_car_id) {
      toast.error('Please select a Mini Cooper from our fleet');
      return;
    }

    setSubmitting(true);
    try {
      if (isSupabaseConfigured) {
        const { error } = await supabase
          .from('matric_bookings')
          .insert([
            {
              customer_name: formData.customer_name,
              customer_email: formData.customer_email,
              customer_phone: formData.customer_phone,
              event_date: formData.event_date,
              pickup_time: formData.pickup_time || null,
              pickup_address: formData.pickup_address,
              destination_address: formData.destination_address,
              hire_car_id: formData.hire_car_id,
              notes: formData.notes || null,
              status: 'pending',
              price_quote: 'POA'
            }
          ]);

        if (error) throw error;
      }

      toast.success('Booking request sent successfully! We will get back to you with a quote soon.');
      // Reset form
      setFormData({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        event_date: '',
        pickup_time: '',
        pickup_address: '',
        destination_address: '',
        hire_car_id: '',
        notes: ''
      });
      setSelectedCarId('');
    } catch (err) {
      console.error('Error submitting booking:', err);
      toast.error('Failed to submit booking request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-20">
      {/* Hero Header */}
      <section className="relative overflow-hidden bg-gradient-to-r from-red-900 via-red-800 to-mini-red dark:from-red-950 dark:to-gray-900 text-white py-20 px-4">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-red-600/20 via-transparent to-transparent pointer-events-none"></div>
        <div className="container mx-auto max-w-5xl text-center relative z-10 space-y-6">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 px-4 py-1.5 rounded-full text-sm font-semibold text-red-200"
          >
            <Sparkles size={16} />
            <span>Matric Dance Chauffeur Hire</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-6xl font-heading font-extrabold tracking-tight leading-tight"
          >
            Arrive in Style in a <br/>
            <span className="bg-gradient-to-r from-red-200 to-white bg-clip-text text-transparent">Classic Mini Cooper</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl text-gray-200 max-w-2xl mx-auto leading-relaxed"
          >
            Make your grand entrance unforgettable. Hire a beautifully restored classic Mini Cooper, complete with a professional chauffeur, for your special night.
          </motion.p>
        </div>
      </section>

      {/* Package Information */}
      <section className="py-16 px-4 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex flex-col items-center text-center p-6 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-100 dark:border-gray-800">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 text-mini-red dark:text-red-400 rounded-xl flex items-center justify-center mb-4">
                <Shield size={24} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Suited Chauffeur</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                Enjoy peace of mind with a professional, fully vetted driver dressed in a smart suit to chauffeur you safely to your venue.
              </p>
            </div>

            <div className="flex flex-col items-center text-center p-6 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-100 dark:border-gray-800">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 text-mini-red dark:text-red-400 rounded-xl flex items-center justify-center mb-4">
                <Sparkles size={24} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">School Color Ribbons</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                We custom-decorate the vehicle with elegant front ribbons matching your school or outfit colors at no additional cost.
              </p>
            </div>

            <div className="flex flex-col items-center text-center p-6 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-100 dark:border-gray-800">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 text-mini-red dark:text-red-400 rounded-xl flex items-center justify-center mb-4">
                <Award size={24} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Photo Opportunities</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                Ample time is allocated at pickup, en-route scenic spots, and the drop-off red carpet for premium photos with the Mini.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Fleet Showcase */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-5xl space-y-12">
          <div className="text-center space-y-4">
            <h2 className="text-3xl font-heading font-extrabold text-gray-900 dark:text-white">Choose Your Classic Mini</h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-xl mx-auto">
              Select from our pristine collection of classic Mini Coopers, each maintained to perfection and ready for your grand entrance.
            </p>
          </div>

          {loadingFleet ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
            </div>
          ) : fleet.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-2xl border p-8">
              <p className="text-gray-500">No classic Minis are currently available for matric bookings. Please contact us directly for enquiries.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {fleet.map((car) => (
                <motion.div
                  key={car.id}
                  className={`bg-white dark:bg-gray-900 rounded-2xl overflow-hidden shadow-lg border-2 transition-all duration-300 ${
                    selectedCarId === car.id ? 'border-red-600 ring-2 ring-red-600/20' : 'border-gray-250 dark:border-gray-800'
                  }`}
                  whileHover={{ y: -5 }}
                >
                  <div className="relative h-64 bg-gray-100 dark:bg-gray-800">
                    {car.image_url ? (
                      <img src={car.image_url} alt={`${car.make} ${car.model}`} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 font-medium">No Image Available</div>
                    )}
                    <div className="absolute top-4 right-4 bg-black/75 backdrop-blur-md text-white px-3 py-1.5 rounded-full text-sm font-bold border border-white/20">
                      POA (Price on Ask)
                    </div>
                  </div>

                  <div className="p-6 space-y-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                        {car.make} {car.model}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {car.year} • {car.color}
                      </p>
                    </div>

                    <p className="text-sm text-gray-600 dark:text-gray-350 leading-relaxed">
                      {car.description || 'A timeless British icon ready to add charm, nostalgia, and elegance to your matric farewell entrance.'}
                    </p>

                    <button
                      onClick={() => handleSelectCar(car.id)}
                      className={`w-full py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all ${
                        selectedCarId === car.id
                          ? 'bg-mini-red text-white hover:bg-red-700 shadow-md shadow-red-500/20'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-250 dark:hover:bg-gray-705'
                      }`}
                    >
                      {selectedCarId === car.id ? 'Selected' : 'Select This Vehicle'}
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Booking Form Section */}
      <section id="booking-form" className="py-16 px-4 bg-gray-100 dark:bg-gray-900/40">
        <div className="container mx-auto max-w-2xl bg-white dark:bg-gray-900 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-800/80 p-6 sm:p-10">
          <div className="text-center mb-8 space-y-2">
            <h2 className="text-3xl font-heading font-extrabold text-gray-900 dark:text-white">Request a Hire Quote</h2>
            <p className="text-gray-600 dark:text-gray-400">
              Submit your itinerary, and we will get back to you with a custom POA quote for your matric farewell.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Contact Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white border-b pb-1 dark:border-gray-800">
                1. Your Contact Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      required
                      placeholder="e.g. John Doe"
                      value={formData.customer_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                      className="w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="tel"
                      required
                      placeholder="e.g. 082 123 4567"
                      value={formData.customer_phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                      className="w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    required
                    placeholder="e.g. john@example.com"
                    value={formData.customer_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Event Itinerary */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white border-b pb-1 dark:border-gray-800">
                2. Itinerary & Route
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date of Event</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="date"
                      required
                      value={formData.event_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, event_date: e.target.value }))}
                      className="w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pickup Time</label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="time"
                      required
                      value={formData.pickup_time}
                      onChange={(e) => setFormData(prev => ({ ...prev, pickup_time: e.target.value }))}
                      className="w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pickup Address</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3.5 w-4 h-4 text-blue-500" />
                  <textarea
                    rows={2}
                    required
                    placeholder="Where should the chauffeur pick you up?"
                    value={formData.pickup_address}
                    onChange={(e) => setFormData(prev => ({ ...prev, pickup_address: e.target.value }))}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Destination Address (Matric Venue)</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3.5 w-4 h-4 text-green-500" />
                  <textarea
                    rows={2}
                    required
                    placeholder="Where is the matric dance venue?"
                    value={formData.destination_address}
                    onChange={(e) => setFormData(prev => ({ ...prev, destination_address: e.target.value }))}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Vehicle & Notes */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white border-b pb-1 dark:border-gray-800">
                3. Vehicle Selection & Notes
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Select Classic Mini Cooper</label>
                <select
                  required
                  value={formData.hire_car_id}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, hire_car_id: e.target.value }));
                    setSelectedCarId(e.target.value);
                  }}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                >
                  <option value="">-- Choose a Mini --</option>
                  {fleet.map((car) => (
                    <option key={car.id} value={car.id}>
                      {car.make} {car.model} ({car.color}) - POA
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Special Requests or Notes</label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3.5 w-4 h-4 text-gray-400" />
                  <textarea
                    rows={3}
                    placeholder="Specify preferred ribbon colors, route details, photo locations, etc."
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-4 bg-gradient-to-r from-red-700 to-mini-red hover:from-red-800 hover:to-red-700 text-white rounded-xl font-bold text-base shadow-lg shadow-mini-red/20 transition-all disabled:opacity-50"
            >
              {submitting ? 'Sending Request...' : 'Submit Booking Request (POA)'}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
